import { useEffect, useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Bell,
  BriefcaseBusiness,
  BusFront,
  CalendarDays,
  CheckCircle2,
  Code2,
  ContactRound,
  Eye,
  EyeOff,
  Home,
  Info,
  KeyRound,
  Landmark,
  Languages,
  LoaderCircle,
  MapPinned,
  Megaphone,
  Radio,
  RefreshCw,
  Save,
  Send,
  Settings2,
  ShoppingBag,
  Smartphone,
  UsersRound,
  Wheat,
} from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { Notice, PageLoader } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import { saveRecord, subscribeToDocument } from '../services/data';
import { getOneSignalStatus, sendCustomPushNotification, setOneSignalConfig } from '../services/adminApi';
import '../app-settings.css';

const SERVICE_OPTIONS = [
  { key: 'businesses', label: 'Business Directory', icon: BriefcaseBusiness, quick: true, menu: false },
  { key: 'apmc', label: 'APMC Prices', icon: Wheat, quick: true, menu: false },
  { key: 'marketplace', label: 'Buy & Sell', icon: ShoppingBag, quick: true, menu: false },
  { key: 'announcements', label: 'Village Announcements', icon: Megaphone, quick: true, menu: true },
  { key: 'contacts', label: 'Important Contacts', icon: ContactRound, quick: true, menu: false },
  { key: 'bus', label: 'Bus Schedule', icon: BusFront, quick: true, menu: true },
  { key: 'events', label: 'Events', icon: CalendarDays, quick: true, menu: false },
  { key: 'places', label: 'Memorable Places', icon: MapPinned, quick: true, menu: false },
  { key: 'managers', label: 'Content Managers', icon: UsersRound, quick: true, menu: true },
  { key: 'developer', label: 'Developer Details', icon: Code2, quick: true, menu: true },
  { key: 'language', label: 'Language', icon: Languages, quick: true, menu: true },
  { key: 'about', label: 'About Village', icon: Landmark, quick: false, menu: true },
];

const ALL_SERVICE_KEYS = SERVICE_OPTIONS.map((item) => item.key);
const QUICK_SERVICE_KEYS = SERVICE_OPTIONS.filter((item) => item.quick).map((item) => item.key);
const SERVICE_MAP = Object.fromEntries(SERVICE_OPTIONS.map((item) => [item.key, item]));

const DEFAULT_SETTINGS = {
  apmcPriceUnit: 'man20',
  enabledServices: [...ALL_SERVICE_KEYS],
  quickServicesOrder: [
    'apmc',
    'businesses',
    'marketplace',
    'announcements',
    'events',
    'contacts',
    'bus',
    'places',
    'managers',
    'developer',
    'language',
  ],
  footerServices: ['businesses', 'apmc', 'contacts', 'managers'],
  isActive: true,
};

function uniqueValid(values, allowed) {
  if (!Array.isArray(values)) return [];
  return values.filter(
    (key, index) => allowed.includes(key) && values.indexOf(key) === index,
  );
}

function normalizeSettings(value) {
  const source = value || {};
  const enabledServices = Array.isArray(source.enabledServices)
    ? uniqueValid(source.enabledServices, ALL_SERVICE_KEYS)
    : [...DEFAULT_SETTINGS.enabledServices];

  const requestedQuick = uniqueValid(source.quickServicesOrder, QUICK_SERVICE_KEYS).filter((key) =>
    enabledServices.includes(key),
  );
  const quickFallback = DEFAULT_SETTINGS.quickServicesOrder.filter(
    (key) => enabledServices.includes(key) && !requestedQuick.includes(key),
  );

  const requestedFooter = uniqueValid(source.footerServices, ALL_SERVICE_KEYS);
  const footerServices = requestedFooter.length
    ? requestedFooter.slice(0, 4)
    : [...DEFAULT_SETTINGS.footerServices];

  return {
    ...DEFAULT_SETTINGS,
    ...source,
    apmcPriceUnit: source.apmcPriceUnit === 'quintal100' ? 'quintal100' : 'man20',
    enabledServices,
    quickServicesOrder: [...requestedQuick, ...quickFallback],
    footerServices,
    isActive: true,
  };
}

function ServiceBadge({ children, tone = 'neutral' }) {
  return <span className={`settings-surface-badge ${tone}`}>{children}</span>;
}

function UnitCard({ active, title, subtitle, badge, onSelect }) {
  return (
    <button
      className={`settings-unit-card ${active ? 'active' : ''}`}
      type="button"
      onClick={onSelect}
      aria-pressed={active}
    >
      <span className="settings-radio-dot"><span /></span>
      <span className="settings-unit-copy">
        <strong>{title}</strong>
        <small>{subtitle}</small>
      </span>
      <span className="settings-unit-badge">{badge}</span>
    </button>
  );
}

export default function AppSettingsPage() {
  const { isSuperAdmin } = useAuth();
  const [form, setForm] = useState(DEFAULT_SETTINGS);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // OneSignal diagnostics state
  const [oneSignalStatus, setOneSignalStatus] = useState(null);
  const [oneSignalChecking, setOneSignalChecking] = useState(false);
  const [restKeyInput, setRestKeyInput] = useState('');
  const [appIdInput, setAppIdInput] = useState('');
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [testTitle, setTestTitle] = useState('ટેસ્ટ સૂચના (Admin Test)');
  const [testMessage, setTestMessage] = useState('આ એડમિન પેનલ તરફથી ટેસ્ટ પુશ નોટિફિકેશન છે.');
  const [testAudience, setTestAudience] = useState('all');
  const [testSegment, setTestSegment] = useState('');
  const [testSubscriptionId, setTestSubscriptionId] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testPushResult, setTestPushResult] = useState(null);

  const checkOneSignal = async () => {
    setOneSignalChecking(true);
    try {
      const status = await getOneSignalStatus();
      setOneSignalStatus(status);
    } catch (err) {
      console.warn('OneSignal status check error:', err);
      setOneSignalStatus({
        appIdConfigured: false,
        restApiKeyConfigured: false,
        apiConnected: false,
        statusError: err.message || 'Status check failed.',
      });
    } finally {
      setOneSignalChecking(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    checkOneSignal();
  }, [isSuperAdmin]);

  const saveOneSignalCredentials = async () => {
    setSavingCredentials(true);
    setError('');
    setSuccess('');
    try {
      await setOneSignalConfig({
        appId: appIdInput.trim() || undefined,
        restApiKey: restKeyInput.trim() || undefined,
      });
      setSuccess('OneSignal credentials successfully saved to secure system settings.');
      setRestKeyInput('');
      await checkOneSignal();
    } catch (err) {
      setError(err.message || 'Failed to save OneSignal credentials.');
    } finally {
      setSavingCredentials(false);
    }
  };

  const handleSendTestPush = async () => {
    setSendingTest(true);
    setTestPushResult(null);
    try {
      const subscriptionIds = testAudience === 'subscription_ids' && testSubscriptionId.trim()
        ? [testSubscriptionId.trim()]
        : [];
      const segment = testAudience === 'custom_segment'
        ? testSegment.trim()
        : (testAudience !== 'all' && testAudience !== 'subscription_ids' ? testAudience : undefined);

      const result = await sendCustomPushNotification({
        title: testTitle.trim(),
        message: testMessage.trim(),
        targetAudience: testAudience === 'subscription_ids' ? 'subscription_ids' : 'all',
        segment,
        subscriptionIds,
      });
      setTestPushResult({
        success: true,
        message: `Test notification sent successfully to ${result.recipients || 0} recipient(s)! (OneSignal ID: ${result.notificationId})`,
      });
    } catch (err) {
      setTestPushResult({
        success: false,
        message: err.message || 'Failed to send test notification.',
      });
    } finally {
      setSendingTest(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    return subscribeToDocument(
      'appSettings',
      'public',
      (record) => {
        setExists(Boolean(record));
        setForm(normalizeSettings(record));
        setLoading(false);
      },
      (snapshotError) => {
        setError(snapshotError.message || 'App settings load thai shakya nahi.');
        setLoading(false);
      },
    );
  }, [isSuperAdmin]);

  const enabledSet = useMemo(() => new Set(form.enabledServices), [form.enabledServices]);
  const enabledQuickServices = useMemo(
    () => form.quickServicesOrder.filter((key) => enabledSet.has(key) && SERVICE_MAP[key]?.quick),
    [enabledSet, form.quickServicesOrder],
  );
  const duplicateFooterService = useMemo(() => {
    const values = form.footerServices.filter(Boolean);
    return values.length !== new Set(values).size;
  }, [form.footerServices]);
  const footerUsesDisabledService = useMemo(
    () => form.footerServices.some((key) => key && !enabledSet.has(key)),
    [enabledSet, form.footerServices],
  );

  if (!isSuperAdmin) return <Navigate to="/unauthorized" replace />;

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const toggleService = (serviceKey) => {
    clearMessages();
    const currentlyEnabled = enabledSet.has(serviceKey);
    const nextEnabled = currentlyEnabled
      ? form.enabledServices.filter((key) => key !== serviceKey)
      : [...form.enabledServices, serviceKey];

    let nextQuick = [...form.quickServicesOrder];
    if (SERVICE_MAP[serviceKey]?.quick) {
      if (currentlyEnabled) nextQuick = nextQuick.filter((key) => key !== serviceKey);
      else if (!nextQuick.includes(serviceKey)) nextQuick.push(serviceKey);
    }

    const nextFooter = currentlyEnabled
      ? form.footerServices.map((key) => (key === serviceKey ? '' : key))
      : form.footerServices;

    setForm({
      ...form,
      enabledServices: nextEnabled,
      quickServicesOrder: nextQuick,
      footerServices: nextFooter,
    });
  };

  const moveQuickService = (serviceKey, direction) => {
    clearMessages();
    const order = [...enabledQuickServices];
    const index = order.indexOf(serviceKey);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= order.length) return;
    [order[index], order[target]] = [order[target], order[index]];

    const hiddenQuick = form.quickServicesOrder.filter((key) => !enabledSet.has(key));
    setForm({ ...form, quickServicesOrder: [...order, ...hiddenQuick] });
  };

  const updateFooterSlot = (index, value) => {
    clearMessages();
    const next = [...form.footerServices];
    next[index] = value;
    setForm({ ...form, footerServices: next });
  };

  const save = async (event) => {
    event.preventDefault();
    clearMessages();

    if (form.enabledServices.length < 4) {
      setError('Footer mate Home sivay minimum 4 services ON rakhvi jaruri chhe.');
      return;
    }
    if (duplicateFooterService) {
      setError('Footer ma same service ek karta vadhu slot ma select kari shakati nathi.');
      return;
    }
    if (form.footerServices.length !== 4 || form.footerServices.some((item) => !item)) {
      setError('Footer mate Position 2 thi Position 5 sudhi badha 4 services select karo.');
      return;
    }
    if (footerUsesDisabledService) {
      setError('Footer ma OFF service select chhe. Enabled service select karo.');
      return;
    }

    setBusy(true);
    try {
      await saveRecord(
        'appSettings',
        {
          id: 'public',
          apmcPriceUnit: form.apmcPriceUnit,
          enabledServices: form.enabledServices,
          quickServicesOrder: enabledQuickServices,
          footerServices: form.footerServices,
          isActive: true,
        },
        exists,
      );
      setExists(true);
      setSuccess('App settings save thai gai. Mobile app ma realtime update thase.');
    } catch (saveError) {
      setError(saveError.message || 'App settings save failed.');
    } finally {
      setBusy(false);
    }
  };

  const footerPreview = form.footerServices
    .filter(Boolean)
    .map((key) => SERVICE_MAP[key]?.label || key);

  return (
    <div className="app-settings-page">
      <section className="settings-hero">
        <div className="settings-hero-icon"><Settings2 size={26} /></div>
        <div className="settings-hero-copy">
          <p className="eyebrow">Mobile application controls</p>
          <h1>App Settings</h1>
          <p>APMC unit, service visibility, Quick Services order ane footer navigation ek j jagya thi control karo.</p>
        </div>
        <div className="settings-hero-stats">
          <div><strong>{form.enabledServices.length}</strong><span>Services ON</span></div>
          <div><strong>{enabledQuickServices.length}</strong><span>Quick Services</span></div>
          <div><strong>5</strong><span>Footer slots</span></div>
        </div>
      </section>

      {error ? <Notice type="error" onClose={() => setError('')}>{error}</Notice> : null}
      {success ? <Notice type="success" onClose={() => setSuccess('')}>{success}</Notice> : null}

      {loading ? <PageLoader label="App settings loading…" /> : (
        <form className="settings-form" onSubmit={save}>
          <section className="settings-card">
            <div className="settings-section-head">
              <div className="settings-section-icon apmc"><Wheat size={21} /></div>
              <div>
                <span className="settings-kicker">APMC PRICE DISPLAY</span>
                <h2>Choose the price unit</h2>
                <p>Government API no raw bhav Quintal (100 KG) ma j rahe chhe. Aa setting only mobile display control kare chhe.</p>
              </div>
            </div>

            <div className="settings-unit-grid">
              <UnitCard
                active={form.apmcPriceUnit === 'quintal100'}
                title="Quintal (100 KG)"
                subtitle="Government API no original bhav as-it-is show thase."
                badge="Original"
                onSelect={() => { clearMessages(); setForm({ ...form, apmcPriceUnit: 'quintal100' }); }}
              />
              <UnitCard
                active={form.apmcPriceUnit === 'man20'}
                title="Man (20 KG)"
                subtitle="100 KG bhav ne 5 thi divide kari 20 KG bhav show thase."
                badge="÷ 5"
                onSelect={() => { clearMessages(); setForm({ ...form, apmcPriceUnit: 'man20' }); }}
              />
            </div>

            <div className="settings-info-strip">
              <Info size={17} />
              <span>Mobile APMC search niche sticky disclaimer selected unit pramane automatically update thase.</span>
            </div>
          </section>

          <section className="settings-card">
            <div className="settings-section-head split">
              <div className="settings-section-title-wrap">
                <div className="settings-section-icon visibility"><Eye size={21} /></div>
                <div>
                  <span className="settings-kicker">GLOBAL SERVICE VISIBILITY</span>
                  <h2>Turn services ON or OFF</h2>
                  <p>Service OFF karta te Footer, Home Quick Services ane available Sidebar entry mathi hide thai jashe.</p>
                </div>
              </div>
              <span className="settings-count-pill">{form.enabledServices.length} / {SERVICE_OPTIONS.length} enabled</span>
            </div>

            <div className="settings-service-grid">
              {SERVICE_OPTIONS.map((service) => {
                const Icon = service.icon;
                const enabled = enabledSet.has(service.key);
                return (
                  <div className={`settings-service-card ${enabled ? 'enabled' : 'disabled'}`} key={service.key}>
                    <div className="settings-service-main">
                      <span className="settings-service-icon"><Icon size={20} /></span>
                      <div>
                        <strong>{service.label}</strong>
                        <div className="settings-surface-row">
                          {service.quick ? <ServiceBadge tone="quick">Quick</ServiceBadge> : null}
                          <ServiceBadge tone="footer">Footer</ServiceBadge>
                          {service.menu ? <ServiceBadge tone="menu">Sidebar</ServiceBadge> : null}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`settings-visibility-toggle ${enabled ? 'on' : 'off'}`}
                      onClick={() => toggleService(service.key)}
                      aria-label={`${enabled ? 'Disable' : 'Enable'} ${service.label}`}
                    >
                      <span>{enabled ? <Eye size={15} /> : <EyeOff size={15} />}</span>
                      <b>{enabled ? 'ON' : 'OFF'}</b>
                    </button>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="settings-card">
            <div className="settings-section-head split">
              <div className="settings-section-title-wrap">
                <div className="settings-section-icon quick"><Smartphone size={21} /></div>
                <div>
                  <span className="settings-kicker">HOME QUICK SERVICES</span>
                  <h2>Quick Services order</h2>
                  <p>Enabled Quick Services ne mobile Home screen par kaya order ma show karva te set karo.</p>
                </div>
              </div>
              <span className="settings-count-pill">{enabledQuickServices.length} visible</span>
            </div>

            <div className="settings-order-list">
              {enabledQuickServices.map((serviceKey, index) => {
                const service = SERVICE_MAP[serviceKey];
                const Icon = service.icon;
                return (
                  <div className="settings-order-row" key={serviceKey}>
                    <span className="settings-order-number">{index + 1}</span>
                    <span className="settings-service-icon compact"><Icon size={18} /></span>
                    <div className="settings-order-copy">
                      <strong>{service.label}</strong>
                      <small>Home Quick Service #{index + 1}</small>
                    </div>
                    <div className="settings-order-actions">
                      <button
                        type="button"
                        onClick={() => moveQuickService(serviceKey, -1)}
                        disabled={index === 0}
                        title="Move up"
                      ><ArrowUp size={17} /></button>
                      <button
                        type="button"
                        onClick={() => moveQuickService(serviceKey, 1)}
                        disabled={index === enabledQuickServices.length - 1}
                        title="Move down"
                      ><ArrowDown size={17} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="settings-card">
            <div className="settings-section-head">
              <div className="settings-section-icon footer"><Smartphone size={21} /></div>
              <div>
                <span className="settings-kicker">MOBILE FOOTER</span>
                <h2>Footer services & order</h2>
                <p>Home Position 1 par permanently fixed chhe. Position 2–5 mate enabled services mathi choose karo.</p>
              </div>
            </div>

            <div className="settings-footer-grid">
              <div className="settings-footer-slot fixed">
                <span className="settings-footer-position">Position 1</span>
                <div className="settings-footer-fixed-value"><Home size={18} /><strong>Home</strong><span>Fixed</span></div>
              </div>

              {form.footerServices.map((value, index) => (
                <label className="settings-footer-slot" key={`footer-slot-${index}`}>
                  <span className="settings-footer-position">Position {index + 2}</span>
                  <select value={value} onChange={(event) => updateFooterSlot(index, event.target.value)}>
                    <option value="">Select enabled service</option>
                    {SERVICE_OPTIONS.filter((service) => enabledSet.has(service.key)).map((service) => {
                      const selectedElsewhere = form.footerServices.some(
                        (item, itemIndex) => itemIndex !== index && item === service.key,
                      );
                      return (
                        <option key={service.key} value={service.key} disabled={selectedElsewhere}>
                          {service.label}
                        </option>
                      );
                    })}
                  </select>
                </label>
              ))}
            </div>

            <div className="settings-footer-preview">
              <div className="settings-preview-title"><CheckCircle2 size={17} /><strong>Footer preview</strong></div>
              <div className="settings-preview-flow">
                <span className="home">Home</span>
                {footerPreview.map((label, index) => (
                  <span key={`${label}-${index}`}>{label}</span>
                ))}
              </div>
            </div>
          </section>

          <section className="settings-card">
            <div className="settings-section-head split">
              <div className="settings-section-title-wrap">
                <div className="settings-section-icon notifications" style={{ color: '#0284c7', backgroundColor: '#e0f2fe' }}><Bell size={21} /></div>
                <div>
                  <span className="settings-kicker">PUSH NOTIFICATION SERVICE</span>
                  <h2>OneSignal Integration & Diagnostics</h2>
                  <p>Check OneSignal connection status, update REST API Key, and send test push notifications directly.</p>
                </div>
              </div>
              <button
                type="button"
                className="button secondary compact"
                onClick={checkOneSignal}
                disabled={oneSignalChecking}
              >
                {oneSignalChecking ? <LoaderCircle className="spin" size={15} /> : <RefreshCw size={15} />}
                <span>Refresh Status</span>
              </button>
            </div>

            <div className="settings-unit-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', marginBottom: 16 }}>
              <div className="panel-card" style={{ padding: 14 }}>
                <small style={{ color: '#6b7280', fontSize: 12 }}>OneSignal App ID</small>
                <div style={{ marginTop: 4, fontWeight: 600, fontSize: 14 }}>
                  {oneSignalStatus?.appId || '48da6188-d11c-4cd1-a4ff-cdb3c0e599e7'}
                </div>
                <span className="settings-surface-badge quick" style={{ marginTop: 6, display: 'inline-block' }}>
                  {oneSignalStatus?.appIdConfigured ? 'App ID Active' : 'Default App ID'}
                </span>
              </div>

              <div className="panel-card" style={{ padding: 14 }}>
                <small style={{ color: '#6b7280', fontSize: 12 }}>REST API Key Status</small>
                <div style={{ marginTop: 4, fontWeight: 600, fontSize: 14 }}>
                  {oneSignalStatus?.restApiKeyConfigured ? (oneSignalStatus.restApiKeyMasked || '••••••••••••') : 'Not Configured'}
                </div>
                <span className={`settings-surface-badge ${oneSignalStatus?.restApiKeyConfigured ? 'quick' : 'menu'}`} style={{ marginTop: 6, display: 'inline-block' }}>
                  {oneSignalStatus?.restApiKeyConfigured ? 'Active / Configured' : 'Missing REST Key'}
                </span>
              </div>

              <div className="panel-card" style={{ padding: 14 }}>
                <small style={{ color: '#6b7280', fontSize: 12 }}>OneSignal API Connection</small>
                <div style={{ marginTop: 4, fontWeight: 600, fontSize: 14, color: oneSignalStatus?.apiConnected ? '#059669' : '#dc2626' }}>
                  {oneSignalStatus?.apiConnected ? `Connected (${oneSignalStatus.appDetails?.messageable_players ?? 0} active subscribers)` : (oneSignalStatus?.statusError || 'Click Refresh Status to test')}
                </div>
                <span className={`settings-surface-badge ${oneSignalStatus?.apiConnected ? 'quick' : 'neutral'}`} style={{ marginTop: 6, display: 'inline-block' }}>
                  {oneSignalStatus?.apiConnected ? 'Live Connection OK' : 'Check Credentials'}
                </span>
              </div>
            </div>

            <div className="panel-card" style={{ padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <KeyRound size={18} color="#d97706" />
                <strong>Configure OneSignal REST API Key</strong>
              </div>
              <p style={{ fontSize: 13, color: '#4b5563', marginBottom: 12 }}>
                You can configure the key via Google Cloud Secrets (<code>ONESIGNAL_REST_API_KEY</code>) or save it below into admin-only Firestore configuration:
              </p>
              <div className="form-grid two">
                <label className="field">
                  <span>OneSignal REST API Key</span>
                  <input
                    type="password"
                    value={restKeyInput}
                    placeholder="Enter OneSignal REST API Key"
                    onChange={(e) => setRestKeyInput(e.target.value)}
                  />
                  <small>Found in OneSignal Dashboard → Settings → Keys & IDs.</small>
                </label>
                <label className="field">
                  <span>OneSignal App ID (Optional override)</span>
                  <input
                    value={appIdInput}
                    placeholder="48da6188-d11c-4cd1-a4ff-cdb3c0e599e7"
                    onChange={(e) => setAppIdInput(e.target.value)}
                  />
                  <small>Leave blank to use default App ID.</small>
                </label>
              </div>
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="button secondary compact"
                  onClick={saveOneSignalCredentials}
                  disabled={savingCredentials || (!restKeyInput.trim() && !appIdInput.trim())}
                >
                  {savingCredentials ? <LoaderCircle className="spin" size={15} /> : <Save size={15} />}
                  <span>Save OneSignal Credentials</span>
                </button>
              </div>
            </div>

            <div className="panel-card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Radio size={18} color="#059669" />
                <strong>Send Test Push Notification</strong>
              </div>
              <p style={{ fontSize: 13, color: '#4b5563', marginBottom: 12 }}>
                Send a real test notification to verify end-to-end delivery to mobile devices:
              </p>
              {testPushResult ? (
                <Notice type={testPushResult.success ? 'success' : 'error'} onClose={() => setTestPushResult(null)}>
                  {testPushResult.message}
                </Notice>
              ) : null}
              <div className="form-grid two">
                <label className="field">
                  <span>Test Title</span>
                  <input
                    value={testTitle}
                    onChange={(e) => setTestTitle(e.target.value)}
                    placeholder="e.g. ટેસ્ટ સૂચના"
                  />
                </label>
                <label className="field">
                  <span>Test Message</span>
                  <input
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    placeholder="e.g. આ એડમિન પેનલ તરફથી ટેસ્ટ પુશ નોટિફિકેશન છે."
                  />
                </label>
              </div>
              <div className="form-grid two" style={{ marginTop: 8 }}>
                <label className="field">
                  <span>Target Audience</span>
                  <select value={testAudience} onChange={(e) => setTestAudience(e.target.value)}>
                    <option value="all">All Subscribed Users (Auto-detect Segment)</option>
                    {Array.isArray(oneSignalStatus?.appDetails?.segments) && oneSignalStatus.appDetails.segments.length > 0 ? (
                      oneSignalStatus.appDetails.segments.map((seg) => (
                        <option key={seg} value={seg}>Segment: {seg}</option>
                      ))
                    ) : null}
                    <option value="custom_segment">Custom Segment Name</option>
                    <option value="subscription_ids">Specific Subscription ID (Testing)</option>
                  </select>
                </label>
                {testAudience === 'custom_segment' ? (
                  <label className="field">
                    <span>Segment Name in OneSignal</span>
                    <input
                      value={testSegment}
                      onChange={(e) => setTestSegment(e.target.value)}
                      placeholder="e.g. Total Subscriptions or Subscribed Users"
                      required
                    />
                  </label>
                ) : null}
                {testAudience === 'subscription_ids' ? (
                  <label className="field">
                    <span>OneSignal Subscription / Player ID</span>
                    <input
                      value={testSubscriptionId}
                      onChange={(e) => setTestSubscriptionId(e.target.value)}
                      placeholder="e.g. 7c30a845-6677-48f5-9ab7-..."
                    />
                  </label>
                ) : null}
              </div>
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="button primary compact"
                  onClick={handleSendTestPush}
                  disabled={sendingTest || !testTitle.trim() || !testMessage.trim()}
                >
                  {sendingTest ? <LoaderCircle className="spin" size={15} /> : <Send size={15} />}
                  <span>{sendingTest ? 'Sending Test Push…' : 'Send Test Notification'}</span>
                </button>
              </div>
            </div>
          </section>

          <div className="settings-save-bar">
            <div>
              <strong>Ready to publish settings?</strong>
              <span>Changes Firebase ma save thase ane supported mobile app versions ma realtime apply thase.</span>
            </div>
            <button
              className="button primary settings-save-button"
              disabled={busy || duplicateFooterService || footerUsesDisabledService}
            >
              {busy ? <LoaderCircle className="spin" size={18} /> : <Save size={18} />}
              {busy ? 'Saving…' : 'Save App Settings'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
